import { FormApi } from "final-form";
import React, { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18n from "src/i18n/config";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { z } from "zod";

import { createDeviceMdmCommand } from "@devices/api/devices";
import { useTheme } from "@mui/material";
import { PrimaryButton } from "@shared/CustomButton";
import { ControlledCheckboxField } from "@shared/form/CheckboxField";
import PasswordField from "@shared/form/PasswordField";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const validationSchema = z.object({
    ssid: z
        .string()
        .min(1, { message: i18n.t("common:validation.fieldRequired") })
        .max(32),
    password: z
        .string()
        .min(8, { message: i18n.t("common:validation.custom.passwordMin") })
        .max(63),
    hidden: z.boolean(),
});

type Props = {
    deviceId: number;
    open: boolean;
    onClose: () => void;
    onCreated: () => Promise<void>;
};

type FormValues = {
    ssid: string;
    password: string;
    hidden: boolean;
};

export const AddWifiNetworkModal = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation(["modals", "common", "sidepanels"]);
    const validate = useValidationSchema(validationSchema);
    const theme = useTheme();

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            setLoading(true);
            await createDeviceMdmCommand({
                deviceId: props.deviceId,
                json: {
                    command: "addWifiNetwork",
                    ssid: values.ssid,
                    password: values.password,
                    security: "WPA",
                    hidden: values.hidden,
                },
            });
            showSuccessToast(t("common:toast.commandEnqueued"));
            form.restart();
            props.onClose();
            await props.onCreated();
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:devicePanel.toast.commandSentFailed"));
        } finally {
            setLoading(false);
        }
    };

    const onClose = (form: FormApi<FormValues>) => {
        form.restart();
        props.onClose();
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ ssid: "", password: "", hidden: false }}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        title={t("modals:addWifiNetwork.title")}
                        description={t("modals:addWifiNetwork.description")}
                        open={props.open}
                        onClose={() => onClose(form)}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        disableEnforceFocus
                        endButton={
                            <PrimaryButton disabled={loading} loading={loading} onClick={form.submit}>
                                {t("common:button.add")}
                            </PrimaryButton>
                        }
                    >
                        <TextField
                            name="ssid"
                            label={t("modals:addWifiNetwork.input.ssid.caption")}
                            fullWidth
                            required
                            style={{ marginTop: theme.spacing(2) }}
                        />
                        <PasswordField name="password" label={t("common:label.password")} fullWidth required />
                        <ControlledCheckboxField name="hidden" label={t("modals:addWifiNetwork.input.hidden.caption")} />
                    </Modal>
                </form>
            )}
        />
    );
};
